class UABaseSearch extends HTMLElement {
  constructor() {
    super();
    this.elementSelectors = {
      headerSearch: 'header-search',
      headerSearchCloseButton: '.header-search__close',
      headerSearchDetails: '.header-search__details',
      headerSearchResults: '.search-results__results',
      headerSearchOverlay: '.header-search__overlay',
      headerSearchInput: '#HeaderSearchSearchInTemplate',
      headerFacetsForm: '.header-search #FacetsSearchSideDrawerFacetFiltersForm',
      pageSearchInput: '#HeaderSearchSearchIn', // for search inputs on pages. Search page.
      facetsSideDrawer: '.facets-side-drawer',
      facetsSearchDrawer: 'facets-search-drawer',
      searchSuggestionList: '.search-suggestion__list',
      productGridTopBar: '.product-grid-top-bar',
      announcementBar: '.section-announcement-bar',
    };

    this.classes = {
      loading: 'loading',
      loaded: 'loaded',
      headerSearchLoading: 'header-search--loading',
      active: 'active',
      headerSearchEmpty: 'header-search--empty',
      headerSearchOpen: 'header-search--open',
      textHighlight: 'highlight',
      headerSearchHideAnnouncementBar: 'header-search--hide-announcement-bar'
    };

    this.searchPreferences = {
      type: 'product', // product, article, page or blank
      options: {
        unavailable_products: 'show', // show, hide
        prefix: 'last', // last, none
      },
      sort_by: 'relevance', // relevance, price-ascending, price-descending
    };

    this.urls = {
      searchPage: `${window.Shopify.routes.root}search`
    };
  }

  setElements() {
    this.headerSearchEl = document.querySelector(this.elementSelectors.headerSearch);
    if (this.headerSearchEl) {
      this.headerSearchCloseButtonEl = this.headerSearchEl.querySelector(this.elementSelectors.headerSearchCloseButton);
      this.headerSearchDetailsEl = this.headerSearchEl.querySelector(this.elementSelectors.headerSearchDetails);
      this.headerSearchInputEl = this.headerSearchEl.querySelector(this.elementSelectors.headerSearchInput);
      this.headerSearchResultsEl = this.headerSearchEl.querySelector(this.elementSelectors.headerSearchResults);
      this.headerSearchOverlayEl = this.headerSearchEl.querySelector(this.elementSelectors.headerSearchOverlay);
    }

    if (this.isSearchPage()) {
      this.pageSearchInputEl = document.querySelector(this.elementSelectors.pageSearchInput);
    }

    this.announcementBar = document.querySelector(this.elementSelectors.announcementBar);
  }

  isAnnouncementBarInViewport() {

    if (!this.announcementBar) {
      return false;
    }

    const rect = this.announcementBar.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;
    const vertInView = (rect.top <= windowHeight) && ((rect.top + rect.height) >= 0);
    const horInView = (rect.left <= windowWidth) && ((rect.left + rect.width) >= 0);
    return (vertInView && horInView);
  }

  isHeaderSearchEmpty() {
    return document.querySelector(this.elementSelectors.headerSearch).classList.contains(this.classes.headerSearchEmpty);
  }

  isHeaderSearchVisible() {
    return document.documentElement.classList.contains(this.classes.headerSearchOpen);
  }

  resetHeaderView() {
    if (this.isHeaderSearchEmpty()) {
      return;
    }

    setTimeout(() => {
      if (!this.isHeaderSearchVisible()) {
        this.headerSearchInputEl.value = '';
      }
    }, 300);

    this.headerSearchEl.classList.add(this.classes.headerSearchEmpty);
    this.headerSearchResultsEl.innerHTML = '';
    // remove facets search drawer
    const facetsDrawer = document.querySelector(this.elementSelectors.facetsSearchDrawer);
    if (facetsDrawer) {
      facetsDrawer.remove();
    }

    const productGridTopBar = document.querySelector(`.header-search ${this.elementSelectors.productGridTopBar}`);
    if (productGridTopBar) {
      productGridTopBar.remove();
    }
  }

  isSearchPage() {
    return window.location.pathname === this.urls.searchPage;
  }

  getSearchResults(searchTerm) {
    const cachedResults = this.checkCachedResults(searchTerm);

    if (cachedResults) {
      this.renderSearchResults(cachedResults);
      this.setLoadingState(this.classes.loaded);
      return;
    }

    this.fetchSearchResultsPage(searchTerm);
  }

  checkCachedResults(searchTerm) {
    return this.cachedResults[searchTerm];
  }

  cacheResults(searchTerm, results, facetsSearchDrawer, productGridTopBar) {
    this.cachedResults[searchTerm] = {
      results,
      facetsSearchDrawer,
      productGridTopBar
    };
  }
}

class UASearchHeader extends UABaseSearch {
  constructor() {
    super();
  }

  connectedCallback() {
    this.setElements(this);

    if (!this.headerSearchDetailsEl || !this.headerSearchCloseButtonEl) {
      return;
    }

    this.headerSearchCloseButtonEl.addEventListener('click', this.oncloseButtonClick.bind(this));
    this.headerSearchOverlayEl.addEventListener('click', this.oncloseButtonClick.bind(this));

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'open') {
          this.onDetailsChange();
        }
      });
    });

    observer.observe(this.headerSearchDetailsEl, { attributes: true });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.headerSearchDetailsEl.open = false;
        this.resetHeaderView();
      }
    });
  }

  oncloseButtonClick() {
    this.headerSearchDetailsEl.open = false;
    this.resetHeaderView();
  }

  onDetailsChange() {
    if (this.headerSearchDetailsEl.open) {
      if (this.isAnnouncementBarInViewport()) {
        document.documentElement.classList.add(this.classes.headerSearchHideAnnouncementBar);
      }
      document.documentElement.classList.add(this.classes.headerSearchOpen);
      this.headerSearchInputEl.focus();
    } else {
      document.documentElement.classList.remove(this.classes.headerSearchOpen);
      document.documentElement.classList.remove(this.classes.headerSearchHideAnnouncementBar);;
    }
  }
}

class UASearchHeaderResults extends UABaseSearch {
  constructor() {
    super();
    this.debounceTimeout = null;
    this.headerSeachEl = this.closest('header-search');
  }

  connectedCallback() {
    this.setElements(this);

    this.cachedResults = {};

    this.searchFormId = this.dataset.searchFormId;
    this.searchFormEl = document.getElementById(this.searchFormId);
    this.searchResultsId = this.dataset.searchResultsId;
    this.headerSearchResultsEl = document.getElementById(this.searchResultsId);
    this.headerSearchInputEl = this.searchFormEl?.querySelector(this.elementSelectors.headerSearchInput);

    if (!this.searchFormEl || !this.headerSearchResultsEl || !this.headerSearchInputEl) {
      return;
    }

    this.isSearchResultsVisible = false;
    this.abortController = new AbortController();
    this.searchTerm = this.headerSearchInputEl.value.trim();

    this.initEventListeners();
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.removeEventListeners();
  }

  initEventListeners() {
    this.searchFormEl.addEventListener('submit', this.onFormSubmit.bind(this));
    this.searchFormEl.addEventListener('reset', this.onFormReset.bind(this));
    this.headerSearchInputEl.addEventListener('input', this.onKeyup.bind(this));
  }

  removeEventListeners() {
    this.searchFormEl.removeEventListener('submit', this.onFormSubmit.bind(this));
    this.searchFormEl.removeEventListener('reset', this.onFormReset.bind(this));
    this.headerSearchInputEl.removeEventListener('input', this.onKeyup.bind(this));
  }

  buildSubmitUrl() {
    const facetsFormEl = document.querySelector(this.elementSelectors.headerFacetsForm);
    const facetsData = new FormData(facetsFormEl);

    let queryString = '';
    for (const [key, value] of facetsData.entries()) {
      queryString += `${encodeURIComponent(key)}=${encodeURIComponent(value)}&`;
    }

    queryString += `type=${this.searchPreferences.type}&`;

    queryString = queryString.slice(0, -1);

    return `${this.urls.searchPage}?${queryString}`;
  }

  onFormSubmit(event) {
    event?.preventDefault();

    if (!this.headerSearchInputEl.value.trim().length || this.querySelector('[aria-selected="true"] a')) {
    } else {
      if (this.loading) {
        this.submitted = true;
        this.headerSearchInputEl.disabled = true;
        return;
      }
      setTimeout(() => {
        if (this.loading) return;
        const submitUrl = this.buildSubmitUrl();
        window.location = submitUrl;
      },1000);
    }
  }

  onFormReset() {
    this.hideSearchResultsWindow();
    this.headerSearchInputEl.focus();
  }

  resetForm() {
    this.searchTerm = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(true);
  }

  onFocusOut() {
    this.hideSearchResultsWindow();
  }

  onKeyup(event) {
    if (!this.headerSearchInputEl.value.trim().length) {
      this.hideSearchResultsWindow(true);
      return;
    }

    if (event.key === 'Enter' || event.key === 'Escape' || event.key === ' ' || event.key === 'Tab') {
      return;
    }

    event.preventDefault();
    this.setLoadingState(this.classes.loading);
    clearTimeout(this.debounceTimeout);

    this.debounceTimeout = setTimeout(() => {
      const newSearchTerm = this.headerSearchInputEl.value.trim();
      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        this.hideSearchResultsWindow(true);
        return;
      }

      if (this.searchTerm.length < 3) {
        this.hideSearchResultsWindow();
        return;
      }

      this.getSearchResults(this.searchTerm);
    }, 300);
  }

  setLoadingState(state) {
    if (state === 'loaded') {
      this.headerSearchEl.classList.remove(this.classes.headerSearchLoading);
      this.loading = false;
      if (this.submitted) this.onFormSubmit();
    } else {
      this.headerSearchEl.classList.add(this.classes.headerSearchLoading);
      this.loading = true;
    }
  }

  fetchSearchResultsPage(searchTerm) {

    fetch(`${this.urls.searchPage}?q=${encodeURIComponent(searchTerm)}&type=${this.searchPreferences.type}`,
        {
          signal: this.abortController.signal
        })
    .then(response => {
      if (!response.ok) {
        this.hideSearchResultsWindow();
        throw new Error(response.status);
      }
      return response.text();
    })
    .then(text => {
      const searchPage = new DOMParser().parseFromString(text, 'text/html');
      const resultsMarkup = searchPage.querySelector('.product-grid-wrap').innerHTML;

      const facetsSearchDrawer = searchPage.querySelector(this.elementSelectors.facetsSearchDrawer).outerHTML;
      const productGridTopBar = searchPage.querySelector(this.elementSelectors.productGridTopBar).outerHTML;

      this.cacheResults(searchTerm, resultsMarkup, facetsSearchDrawer, productGridTopBar);
      const cachedResults = this.checkCachedResults(searchTerm);
      this.renderSearchResults(cachedResults);

      // Datalayer tracking
      document.dispatchEvent(new CustomEvent('update-tracking-buttons'));
    })
    .catch(error => {
      if (error?.code !== 20) {
        this.hideSearchResultsWindow();
        throw error;
      }
    });
  }

  renderSearchResults(resultsMarkup) {
    this.headerSearchResultsEl.innerHTML = resultsMarkup.results;

    const facetsDrawer = document.querySelector('.facets-drawer');
    if (facetsDrawer) {
      facetsDrawer.remove();
    }
    if (resultsMarkup.facetsSearchDrawer) {
      const facetsDrawer = document.createElement('div');
      facetsDrawer.innerHTML = resultsMarkup.facetsSearchDrawer;
      document.body.appendChild(facetsDrawer);
    }

    const productGridTopBar = document.querySelector(`.header-search ${this.elementSelectors.productGridTopBar}`);
    if (productGridTopBar) {
      productGridTopBar.remove();
    }

    this.renderWishlistButtons();
    this.renderTrustPilotBadges();

    if (resultsMarkup.productGridTopBar) {
      // append to headerSearchResultsEl
      const productGridTopBar = document.createElement('div');
      productGridTopBar.innerHTML = resultsMarkup.productGridTopBar;
      this.headerSearchEl.appendChild(productGridTopBar);
    }

    this.querySelector(this.elementSelectors.facetsSideDrawer)?.classList.add(this.classes.active);
    document.querySelector(this.elementSelectors.headerSearch).classList.remove(this.classes.headerSearchEmpty);
    this.setAttribute('results', true);
    this.showSearchResults();
  }

  renderWishlistButtons() {
    document.dispatchEvent(new CustomEvent("swym:refresh-products", {
      detail: {
        selector: 'header-search #product-grid'
      }
    }));
  }

  renderTrustPilotBadges() {
    document.dispatchEvent(new CustomEvent('trustpilot:render-product-mini-widgets', {
      detail: {
        productContainerSelector: 'header-search #product-grid'
      }
    }));
  }

  showSearchResults() {
    this.setAttribute('open', true);
    this.headerSearchInputEl.setAttribute('aria-expanded', true);
    this.querySelector('[data-columns-desktop]').setAttribute('data-columns-desktop', 4);
    this.isSearchResultsVisible = true;
    this.setLoadingState(this.classes.loaded);
  }

  hideSearchResultsWindow(clearSearchTerm = false) {
    if (!this.isSearchResultsVisible) {
      this.setLoadingState(this.classes.loaded);
      return;
    }

    this.closeResults(clearSearchTerm);
    this.isSearchResultsVisible = false;
    this.setLoadingState(this.classes.loaded);
    this.resetHeaderView();
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.headerSearchInputEl.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.headerSearchInputEl.setAttribute('aria-activedescendant', '');
    this.removeAttribute(this.classes.loading);
    this.removeAttribute('open');
    this.headerSearchInputEl.setAttribute('aria-expanded', false);
  }
}

class UASearchHeaderViewAllButton extends UABaseSearch {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('click', this.onViewAllClick.bind(this));
  }

  onViewAllClick() {
    const headerSearchResultsEl = document.querySelector('.header-search search-results');
    headerSearchResultsEl.onFormSubmit();
  }
}

customElements.define("header-search", UASearchHeader);
customElements.define('search-results', UASearchHeaderResults);
customElements.define("header-search-view-all-button", UASearchHeaderViewAllButton);


class UABaseSearchSuggestions extends HTMLElement {
  constructor() {
    super();
    this.elementSelectors = {
      searchInput: '#HeaderSearchSearchInTemplate',
      searchSuggestionList: '.search-suggestion__list',
    };

    this.classes = {
      headerSearchOpen: 'header-search--open',
    };


    this.urls = {
      searchPage: '/search',
    };

    if (this.isSearchPage() && !this.isHeaderSearchVisible()) {
      this.elementSelectors.searchInput = '#HeaderSearchSearchIn';
    }

    this.classes = {
      textHighlight: 'highlight',
    };
  }

  setElements(element) {
    this.searchTerm = this.dataset.searchTerm.toLowerCase();
    this.scriptEl = this.querySelector('script[type="application/json"]');
    this.jsonSuggestions = this.getJsonSuggestions();
    this.liTemplate = this.getLiTemplate();
  }

  getJsonSuggestions() {

    if (this.scriptEl) {
      let rawData = this.scriptEl.innerHTML.trim();
      if (rawData) {
        rawData = `[${rawData}]`;
        try {
          const parsedArray = JSON.parse(rawData);
          const splitData = parsedArray[0].split('},{').map((item, index, array) => {
            if (index === 0) return `${item}}`;
            if (index === array.length - 1) return `{${item}`;
            return `{${item}}`;
          });

          const parsedData = splitData.map(item => JSON.parse(item));
          return parsedData;
        } catch (e) {
          console.error('Failed to parse JSON', e);
          return null;
        }
      }
    }
    return null;
  }

  getLiTemplate() {
    const templateEl = this.querySelector('template');
    if (templateEl) {
      return templateEl.innerHTML;
    } else {
      console.error('Template not found');
    }
  }

  isHeaderSearchVisible() {
    return document.documentElement.classList.contains(this.classes.headerSearchOpen);
  }

  isSearchPage() {
    return window.location.pathname === this.urls.searchPage;
  }
}

class UASearchSuggestions extends UABaseSearchSuggestions {
  constructor() {
    super();
  }

  connectedCallback() {
    this.setElements(this);
    this.renderSuggestions();
  }

  renderSuggestions() {
    if (!this.jsonSuggestions || !this.liTemplate) return;

    const suggestionsContainer = document.createElement('div');
    let suggestions = [];
    let defaultSuggestion = null;

    const searchTermWords = this.searchTerm.split(' ');

    searchTermWords.forEach((word, index) => {
      if (word === '') {
        searchTermWords.splice(index, 1);
      }
    });

    // Collect suggestions that exactly match the search term first
    this.jsonSuggestions.forEach(suggestionObj => {
      if (suggestionObj.search_term === this.searchTerm) {
        suggestions.push(...suggestionObj.suggestions.map(s => ({ suggestion: s, score: Infinity })));
      }
      if (suggestionObj.default) {
        defaultSuggestion = suggestionObj.suggestions;
      }
    });

    // If we have fewer than 5 suggestions, collect close matches
    if (suggestions.length < 5) {
      this.jsonSuggestions.forEach(suggestionObj => {
        if (suggestionObj.search_term !== this.searchTerm) {
          suggestionObj.suggestions.forEach(suggestion => {
            if (suggestion.toLowerCase() === this.searchTerm) {
              return; // Skip exact matches
            }

            const suggestionWords = suggestion.toLowerCase().split(' ');
            const commonWordsCount = suggestionWords.filter(word => searchTermWords.includes(word)).length;

            // Calculate a score based on the number of common words, order of appearance, and character matches
            let orderScore = 0;
            searchTermWords.forEach((word, index) => {
              if (suggestionWords.includes(word)) {
                orderScore += suggestionWords.indexOf(word) === index ? 1 : 0.5;
              }
            });

            const commonCharsCount = [...this.searchTerm].filter(char => suggestion.toLowerCase().includes(char)).length;

            const score = commonWordsCount + orderScore + commonCharsCount;
            suggestions.push({ suggestion, score });
          });
        }
      });
    }

    // Sort suggestions by score (highest score first)
    suggestions.sort((a, b) => b.score - a.score);

    // Limit to at most 5 suggestions
    suggestions = suggestions.slice(0, 5);

    // Wrap matching words with <span> tags
    suggestions.forEach(({ suggestion }) => {
      let wrappedSuggestion = suggestion;
      const regex = new RegExp(`(${this.searchTerm.split(' ').join('|')})`, 'gi');

      // prevent empty span tags, check before using regex
      if (searchTermWords.length > 0) {
        wrappedSuggestion = wrappedSuggestion.replace(regex, `<span class="${this.classes.textHighlight}">$1</span>`);
      }

      const liHtml = this.liTemplate.replace('{ search_suggestion }', wrappedSuggestion);
      const liElement = document.createElement('div');
      liElement.innerHTML = liHtml;
      suggestionsContainer.appendChild(liElement.firstElementChild);
    });

    // Check if there are any highlighted suggestions
    const hasHighlightedSuggestions = suggestionsContainer.innerHTML.includes(`<span class="${this.classes.textHighlight}">`);

    // If no highlighted suggestions and a default exists, use the default suggestions
    if (!hasHighlightedSuggestions && defaultSuggestion) {
      suggestionsContainer.innerHTML = '';
      defaultSuggestion.forEach(suggestion => {
        const wrappedSuggestion = suggestion;  // No highlighting for default suggestions
        const liHtml = this.liTemplate.replace('{ search_suggestion }', wrappedSuggestion);
        const liElement = document.createElement('div');
        liElement.innerHTML = liHtml;
        suggestionsContainer.appendChild(liElement.firstElementChild);
      });
    }

    const ulEl = this.querySelector(this.elementSelectors.searchSuggestionList);
    if (ulEl) {
      ulEl.innerHTML = '';
      while (suggestionsContainer.firstChild) {
        ulEl.appendChild(suggestionsContainer.firstChild);
      }
    } else {
      console.error(`UL element with class "${this.elementSelectors.searchSuggestionList}" not found`);
    }
  }
}

class UASearchSuggestButton extends UABaseSearchSuggestions {
  constructor() {
    super();
  }

  connectedCallback() {
    this.searchInputEl = document.querySelector(this.elementSelectors.searchInput);
    this.addEventListener('click', this.handleClick);
  }

  handleClick() {
    this.searchTerm = this.innerText;

    if (this.searchInputEl) {
      this.searchInputEl.value = this.searchTerm;
      // if not mobile < 750px
      if (window.innerWidth >= 750) {
        this.searchInputEl.focus();
      }

      this.searchInputEl.dispatchEvent(new KeyboardEvent('keyup'));
    }
  }
}

customElements.define('search-suggestions', UASearchSuggestions);
customElements.define('search-suggest-button', UASearchSuggestButton);
